

import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

type Rarity = 'PROSPECT' | 'ROOKIE' | 'VETERAN' | 'ALL_STAR' | 'GOAT';

interface RarityGlowProps {
  rarity: Rarity;
  className?: string;
  children: React.ReactNode;
}

const RARITY_COLORS: Record<Rarity, {
  glow: string;
  gradient: string;
  border: string;
  text: string;
}> = {
  PROSPECT: {
    glow: 'shadow-[#9BA3AF]/30',
    gradient: 'from-[#9BA3AF] to-[#6B7280]',
    border: 'border-[#9BA3AF]',
    text: 'text-[#9BA3AF]',
  },
  ROOKIE: {
    glow: 'shadow-[#2563EB]/50',
    gradient: 'from-[#3B82F6] to-[#1D4ED8]',
    border: 'border-[#2563EB]',
    text: 'text-[#2563EB]',
  },
  VETERAN: {
    glow: 'shadow-[#DC2626]/50',
    gradient: 'from-[#EF4444] to-[#B91C1C]',
    border: 'border-[#DC2626]',
    text: 'text-[#DC2626]',
  },
  ALL_STAR: {
    glow: 'shadow-[#059669]/50',
    gradient: 'from-[#10B981] to-[#047857]',
    border: 'border-[#059669]',
    text: 'text-[#059669]',
  },
  GOAT: {
    glow: 'shadow-gold/60',
    gradient: 'from-gold to-gold-hover',
    border: 'border-gold',
    text: 'text-gold',
  },
};

export function RarityGlow({ rarity, className, children }: RarityGlowProps) {
  const colors = RARITY_COLORS[rarity];

  return (
    <div className={cn('relative', className)}>
      {/* Outer glow */}
      <motion.div
        className={cn(
          'absolute -inset-2 rounded-2xl blur-xl opacity-50',
          'bg-gradient-to-r',
          colors.gradient
        )}
        animate={{
          scale: [1, 1.05, 1],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />

      {/* Inner content */}
      <div
        className={cn(
          'relative shadow-2xl',
          colors.glow
        )}
      >
        {children}
      </div>

      {/* Sparkle effects for rare+ */}
      {rarity !== 'PROSPECT' && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-2xl">
          {[...Array(rarity === 'GOAT' ? 8 : rarity === 'ALL_STAR' ? 6 : 4)].map((_, i) => (
            <motion.div
              key={i}
              className={cn(
                'absolute w-1 h-1 rounded-full',
                'bg-gradient-to-r',
                colors.gradient
              )}
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                opacity: [0, 1, 0],
                scale: [0, 1.5, 0],
              }}
              transition={{
                duration: 1.5 + Math.random(),
                repeat: Infinity,
                delay: Math.random() * 2,
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function getRarityColors(rarity: Rarity) {
  return RARITY_COLORS[rarity];
}
